import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Loader2,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Delete as Backspace,
  PhoneCall
} from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import { ensureWavoip, formatarParaE164 } from "../../services/wavoipClient";
import { startRingtone, stopRingtone } from "../../services/ringtone";

const ESTADOS = {
  conectando: "Conectando…",
  chamando: "Chamando…",
  falando: "Em conversa",
  encerrada: "Chamada encerrada",
  cancelada: "Cancelada por você",
  recusada: "Recusada",
  nao_atendida: "Não atendida",
  erro: "Falha ao ligar"
};

const ESTADOS_FINAIS = new Set(["encerrada", "cancelada", "recusada", "nao_atendida", "erro"]);

const formatarDuracao = (segundos) => {
  const m = String(Math.floor(segundos / 60)).padStart(2, "0");
  const s = String(segundos % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const TECLAS = [
  ["1", ""],
  ["2", "ABC"],
  ["3", "DEF"],
  ["4", "GHI"],
  ["5", "JKL"],
  ["6", "MNO"],
  ["7", "PQRS"],
  ["8", "TUV"],
  ["9", "WXYZ"],
  ["*", ""],
  ["0", "+"],
  ["#", ""],
];

const DialPad = () => {
  const [aberto, setAberto] = useState(false);
  const [numero, setNumero] = useState("");
  const [conexoes, setConexoes] = useState([]);
  const [conexaoSelecionadaId, setConexaoSelecionadaId] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [estado, setEstado] = useState("");
  const [mudo, setMudo] = useState(false);
  const [duracao, setDuracao] = useState(0);

  const saidaRef = useRef(null);
  const ativaRef = useRef(null);
  const cronometro = useRef(null);
  const timeoutProgressoRef = useRef(null);
  const unsubsRef = useRef([]);

  // Estado do drag (draggável pela tela). O drag só começa a partir do
  // pointerdown na barra de título. O offset é aplicado como translate3d no
  // DialogContent, sem alterar layout.
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef(null);
  const dialogContentRef = useRef(null);

  const onDragTitlePointerDown = (e) => {
    // Só botão principal do mouse / toque / caneta.
    if (e.button !== undefined && e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_e) {}
    dragStateRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: dragOffset.x,
      origY: dragOffset.y
    };
  };
  const onDragTitlePointerMove = (e) => {
    const s = dragStateRef.current;
    if (!s) return;
    setDragOffset({
      x: s.origX + (e.clientX - s.startClientX),
      y: s.origY + (e.clientY - s.startClientY)
    });
  };
  const onDragTitlePointerUp = (e) => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_e) {}
  };

  const conexoesComToken = useMemo(
    () => conexoes.filter((c) => c.hasToken && c.token),
    [conexoes]
  );
  const conexaoAtual = useMemo(
    () => conexoesComToken.find((c) => String(c.whatsappId) === String(conexaoSelecionadaId)) || null,
    [conexoesComToken, conexaoSelecionadaId]
  );
  const digitando = estado !== "chamando" && estado !== "falando" && estado !== "conectando";
  const numeroE164 = formatarParaE164(numero);
  const podeLigar = Boolean(conexaoAtual?.token) && numeroE164.length >= 12;

  const carregarConexoes = useCallback(async () => {
    try {
      setCarregando(true);
      const { data } = await api.get("/wavoip", {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Cache-Control": "no-cache, no-store",
          Pragma: "no-cache"
        }
      });
      const lista = Array.isArray(data?.connections) ? data.connections : [];
      setConexoes(lista);
      if (!conexaoSelecionadaId) {
        const primeira = lista.find((c) => c.hasToken && c.token);
        if (primeira) setConexaoSelecionadaId(String(primeira.whatsappId));
      }
    } catch (err) {
      const ct = String(err?.response?.headers?.["content-type"] || "");
      const wasHtml = /text\/html/i.test(ct);
      if (wasHtml) {
        toast.error(
          "Wavoip: service worker retornou página em vez de JSON. Limpe 'Clear site data'."
        );
      } else {
        toast.error(
          `Wavoip: falha ao carregar conexões: ${err?.message || "erro"}`
        );
      }
    } finally {
      setCarregando(false);
    }
  }, [conexaoSelecionadaId]);

  useEffect(() => {
    if (aberto) carregarConexoes();
  }, [aberto, carregarConexoes]);

  const limparTimeoutProgresso = () => {
    if (timeoutProgressoRef.current) {
      clearTimeout(timeoutProgressoRef.current);
      timeoutProgressoRef.current = null;
    }
  };
  const pararCronometro = () => {
    if (cronometro.current) { clearInterval(cronometro.current); cronometro.current = null; }
  };
  const iniciarCronometro = () => {
    pararCronometro();
    setDuracao(0);
    cronometro.current = setInterval(() => setDuracao((d) => d + 1), 1000);
  };
  const unsubscribeTudo = () => {
    while (unsubsRef.current.length) {
      const fn = unsubsRef.current.pop();
      try { fn(); } catch (_e) {}
    }
  };
  const adicionarUnsub = (fn) => {
    if (typeof fn === "function") unsubsRef.current.push(fn);
  };

  const desligar = useCallback(() => {
    pararCronometro();
    limparTimeoutProgresso();
    unsubscribeTudo();
    const ativa = ativaRef.current;
    ativaRef.current = null;
    if (ativa) { try { ativa.end?.(); } catch (_e) {} try { ativa.cancel?.(); } catch (_e) {} }
    const saida = saidaRef.current;
    saidaRef.current = null;
    if (saida) { try { saida.cancel?.(); } catch (_e) {} try { saida.end?.(); } catch (_e) {} }
  }, []);

  useEffect(() => {
    return () => desligar();
  }, [desligar]);

  const voltarAoTeclado = useCallback(() => {
    desligar();
    setEstado("");
    setDuracao(0);
    setMudo(false);
  }, [desligar]);

  const encerrar = useCallback(() => {
    // Se o usuário clicar em "Encerrar" durante conectando/chamando/falando,
    // cancela a chamada e marca "cancelada" visível (não some sozinho). Se o
    // estado já é final, só garante limpeza (não altera o status mostrado).
    const estadoAtual = estado;
    desligar();
    setDuracao(0);
    setMudo(false);
    if (!ESTADOS_FINAIS.has(estadoAtual)) {
      setEstado("cancelada");
    }
  }, [desligar, estado]);

  const aoFecharDialog = (v) => {
    if (!v) {
      // Fechar o modal limpa tudo (não mantém o estado "cancelada" aberta
      // sozinha). Zera também o offset do drag para reabrir centralizado.
      desligar();
      setEstado("");
      setDuracao(0);
      setMudo(false);
      setDragOffset({ x: 0, y: 0 });
      setAberto(false);
    } else {
      setAberto(true);
    }
  };

  // Ringtone (chamando). Respeita a máquina de estado do componente: só toca
  // enquanto aguarda o par atender. Em qualquer outro estado (em conversa,
  // recusada, etc.) ele para imediatamente.
  useEffect(() => {
    if (estado === "chamando") {
      try { startRingtone(); } catch (_e) {}
      return () => stopRingtone();
    }
    stopRingtone();
    return undefined;
  }, [estado]);

  const abrir = () => setAberto(true);

  const apertarTecla = (tecla) => {
    if (!digitando) return;
    if (tecla === "+") {
      if (numero.length === 0) setNumero("+");
      return;
    }
    setNumero((n) => (n + tecla).replace(/[^\d+*#]/g, "").slice(0, 20));
  };

  const apagar = () => {
    if (!digitando) return;
    setNumero((n) => n.slice(0, -1));
  };

  const ligar = async () => {
    if (!podeLigar) {
      if (!conexaoAtual?.token) toast.warn("Escolha uma conexão com token WaVoip.");
      else if (!numeroE164 || numeroE164.length < 12) toast.warn("Digite um número de telefone válido.");
      return;
    }
    desligar();
    setEstado("conectando");
    setDuracao(0);
    setMudo(false);

    try {
      const wav = await ensureWavoip(conexoesComToken.map((c) => c.token));
      setEstado("chamando");
      limparTimeoutProgresso();
      timeoutProgressoRef.current = setTimeout(() => {
        try {
          if (estado !== "falando" && estado !== "encerrada") {
            toast.warn("Sem progresso da Wavoip após 8s. Verifique token/conexão.");
          }
        } catch (_e) {}
      }, 8000);

      const { call, err } = await wav.startCall({
        to: numeroE164,
        fromTokens: [conexaoAtual.token]
      });

      if (err || !call) {
        limparTimeoutProgresso();
        const razoes = err?.devices?.map((d) => `${d.token?.slice(0, 8)}… → ${d.reason}`).join("; ") || "";
        toast.error(
          `Wavoip recusou a chamada: ${err?.message || "sem detalhes"}${razoes ? ` — ${razoes}` : ""}`
        );
        setMudo(false);
        setDuracao(0);
        setEstado("erro");
        return;
      }

      saidaRef.current = call;

      adicionarUnsub(call.on("peerAccept", (ativa) => {
        try {
          limparTimeoutProgresso();
          ativaRef.current = ativa;
          setEstado("falando");
          iniciarCronometro();
          setMudo(false);
          adicionarUnsub(ativa.on("ended", () => {
            limparTimeoutProgresso(); pararCronometro(); setEstado("encerrada");
          }));
          adicionarUnsub(ativa.on("peerMute", () => setMudo(true)));
          adicionarUnsub(ativa.on("peerUnmute", () => setMudo(false)));
          adicionarUnsub(ativa.on("error", (reason) => {
            toast.error(`Falha na ligação: ${String(reason || "erro transporte")}`);
            limparTimeoutProgresso(); pararCronometro(); setEstado("erro");
          }));
          adicionarUnsub(ativa.on("connectionStatus", (s) => {
            if (s === "reconnecting") toast.warn("Mídia reconectando…");
          }));
        } catch (_e) {}
      }));

      adicionarUnsub(call.on("peerReject", () => {
        try { limparTimeoutProgresso(); pararCronometro(); setEstado("recusada"); } catch (_e) {}
      }));
      adicionarUnsub(call.on("unanswered", () => {
        try { limparTimeoutProgresso(); pararCronometro(); setEstado("nao_atendida"); } catch (_e) {}
      }));
      adicionarUnsub(call.on("ended", () => {
        try { limparTimeoutProgresso(); pararCronometro(); if (!ESTADOS_FINAIS.has(estado)) setEstado("encerrada"); } catch (_e) {}
      }));
      adicionarUnsub(call.on("status", (s) => {
        try {
          if (s === "CALLING" || s === "RINGING") {
            if (estado !== "falando") setEstado("chamando");
            return;
          }
          if (s === "ACTIVE") setEstado("falando");
          if (s === "NOT_ANSWERED") { limparTimeoutProgresso(); pararCronometro(); setEstado("nao_atendida"); }
          else if (s === "REJECTED") { limparTimeoutProgresso(); pararCronometro(); setEstado("recusada"); }
          else if (s === "CANCELLED") { limparTimeoutProgresso(); pararCronometro(); setEstado("cancelada"); }
          else if (s === "FAILED" || s === "DISCONNECTED") { limparTimeoutProgresso(); pararCronometro(); setEstado("erro"); }
          else if (s === "ENDED") { limparTimeoutProgresso(); pararCronometro(); if (!ESTADOS_FINAIS.has(estado)) setEstado("encerrada"); }
        } catch (_e) {}
      }));
    } catch (err) {
      toast.error(`Não foi possível iniciar: ${err?.message || "erro desconhecido"}`);
      setMudo(false);
      setDuracao(0);
      setEstado("erro");
    }
  };

  const alternarMudo = async () => {
    const alvo = ativaRef.current || saidaRef.current;
    if (!alvo) return;
    try {
      const r = mudo ? await alvo.unmute() : await alvo.mute();
      if (r?.err) throw new Error(r.err);
      setMudo((m) => !m);
    } catch (err) {
      toast.error(`Não foi possível mudar o microfone: ${err?.message || "erro"}`);
    }
  };

  const ehEstadoFinal = ESTADOS_FINAIS.has(estado);
  const iconeCirculoCor = cn(
    "flex h-16 w-16 items-center justify-center rounded-full",
    estado === "falando" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    estado === "chamando" && "bg-sky-500/10 text-sky-600",
    estado === "conectando" && "bg-muted text-muted-foreground",
    estado === "encerrada" && "bg-slate-500/10 text-slate-600",
    estado === "cancelada" && "bg-amber-500/10 text-amber-600",
    estado === "recusada" && "bg-rose-500/10 text-rose-600",
    estado === "nao_atendida" && "bg-orange-500/10 text-orange-600",
    estado === "erro" && "bg-red-500/10 text-red-600",
  );

  const ChamadaAtivaUI = (
    <div className="flex flex-col items-center gap-4 py-2">
      <span className={iconeCirculoCor}>
        {estado === "conectando" ? (
          <Loader2 className="h-7 w-7 animate-spin" />
        ) : (
          <Phone
            className={cn("h-7 w-7", estado === "chamando" && "animate-pulse")}
          />
        )}
      </span>
      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          De: <span className="tabular-nums font-medium text-foreground">{conexaoAtual?.name || "—"}</span>
        </p>
        <p className="text-base font-semibold tabular-nums break-all">
          Para: {numeroE164 || numero || "—"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{ESTADOS[estado] || "Discador"}</p>
        {estado === "falando" && (
          <p className="text-sm tabular-nums text-muted-foreground">
            {formatarDuracao(duracao)}
          </p>
        )}
        {(estado === "nao_atendida" || estado === "recusada" || estado === "cancelada" || estado === "erro") && duracao > 0 && (
          <p className="text-xs tabular-nums text-muted-foreground">
            Duração: {formatarDuracao(duracao)}
          </p>
        )}
      </div>

      {ehEstadoFinal ? (
        <div className="flex w-full gap-2">
          <Button variant="secondary" className="flex-1" onClick={voltarAoTeclado}>
            <Phone className="h-4 w-4" />
            Nova ligação
          </Button>
        </div>
      ) : (
        <div className="flex w-full gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={alternarMudo}
            disabled={estado !== "falando"}
          >
            {mudo ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {mudo ? "Reativar" : "Mudo"}
          </Button>
          <Button variant="destructive" className="flex-1" onClick={encerrar}>
            <PhoneOff className="h-4 w-4" />
            {estado === "falando" ? "Encerrar" : "Cancelar"}
          </Button>
        </div>
      )}
    </div>
  );

  const DiscadorUI = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">
          Conexão WaVoip
        </label>
        <Select
          value={conexaoSelecionadaId}
          onValueChange={(v) => setConexaoSelecionadaId(v)}
          disabled={carregando}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={carregando ? "Carregando…" : "Escolha uma conexão"} />
          </SelectTrigger>
          <SelectContent>
            {conexoesComToken.length === 0 && !carregando && (
              <div className="px-2 py-3 text-xs text-muted-foreground">
                Nenhuma conexão com token WaVoip ativo.
              </div>
            )}
            {conexoesComToken.map((c) => (
              <SelectItem key={c.whatsappId} value={String(c.whatsappId)}>
                <div className="flex items-center justify-between gap-3 w-full pr-2">
                  <span className="font-medium text-sm">{c.name || `Conexão ${c.whatsappId}`}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {c.channel}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-muted/40 px-4 py-3 min-h-[56px] flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0 truncate text-center text-2xl font-semibold tabular-nums tracking-tight">
          {numero || <span className="text-muted-foreground font-normal text-base">Digite o número</span>}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 h-9 w-9 text-muted-foreground"
          onClick={apagar}
          disabled={!numero || !digitando}
          aria-label="Apagar dígito"
        >
          <Backspace className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 px-2">
        {TECLAS.map(([t, sub]) => (
          <button
            key={t + sub}
            type="button"
            onClick={() => apertarTecla(t)}
            disabled={!digitando}
            className={cn(
              "h-14 rounded-full border bg-background hover:bg-muted/70 active:bg-muted disabled:opacity-40 flex flex-col items-center justify-center gap-0.5 select-none transition-colors"
            )}
          >
            <span className="text-xl font-semibold leading-none">{t}</span>
            {sub && (
              <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                {sub}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-center pt-1">
        <Button
          size="lg"
          onClick={ligar}
          disabled={!podeLigar || !digitando}
          className={cn(
            "h-16 w-16 rounded-full p-0 shadow-md",
            podeLigar
              ? "bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-600/60"
              : ""
          )}
          aria-label="Ligar"
        >
          <PhoneCall className="h-7 w-7" />
        </Button>
      </div>
    </div>
  );

  // Mostra a UI de ligação (e não o teclado) sempre que o estado for
  // diferente de "ocioso" — inclusive os estados finais, para o usuário ver
  // "Não atendida", "Recusada", etc. com botão "Nova ligação".
  const temLigacao = estado !== "";

  // Aplica o offset do drag via translate3d (GPU friendly). O DialogContent
  // do shadcn já é position fixed; só adicionamos translate.
  const dragStyle =
    dragOffset.x !== 0 || dragOffset.y !== 0
      ? { transform: `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)` }
      : undefined;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        aria-label="Abrir teclado para ligação"
        title="Discador WaVoip"
        onClick={abrir}
      >
        <Phone className="h-5 w-5" />
      </Button>

      <Dialog open={aberto} onOpenChange={aoFecharDialog}>
        <DialogContent
          ref={dialogContentRef}
          style={dragStyle}
          className={cn("sm:max-w-sm select-none", temLigacao && "sm:max-w-sm")}
        >
          <DialogTitle
            className="text-base font-semibold cursor-grab active:cursor-grabbing -mt-0.5 touch-none select-none"
            onPointerDown={onDragTitlePointerDown}
            onPointerMove={onDragTitlePointerMove}
            onPointerUp={onDragTitlePointerUp}
            onPointerCancel={onDragTitlePointerUp}
          >
            {temLigacao ? "Ligação WaVoip" : "Discador WaVoip"}
          </DialogTitle>
          {temLigacao ? ChamadaAtivaUI : DiscadorUI}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DialPad;
